<template>
  <form-block
    v-show="isVisible"
    :title="tr('Ad sources')"
    :description="tr('Connect ad sources to include more information in your ad that can help inspire action.')"
  >
    <template #top-right>
      <refresh-button
        :label="tr('Update catalogues')"
        :success-message="tr('Catalogues updated successfully.')"
        :promise="() => refreshCatalogues()"
      />
    </template>

    <div class="flex flex-col gap-1.5">
      <ui-label>{{ tr('Catalogue') }}</ui-label>
      <p class="m-0 text-sm text-muted-foreground">{{ tr('Select a catalogue to show catalogue items with your creative.') }}</p>
      <select-field
        v-model="entity.settings.ad_content_product_catalogue_id"
        :options="cataloguesOptions"
        :reduce="(obj) => obj.id"
        label="name"
        :aria-label="tr('Catalogue')"
        :disabled="disabled || isRefreshing"
        :loading="isLoading || isRefreshing"
      />
    </div>

    <div class="flex flex-col gap-1.5">
      <ui-label>{{ tr('Product set') }}</ui-label>
      <p class="m-0 text-sm text-muted-foreground">{{ tr('Use a product set to feature certain products in your ads and shops on Facebook and Instagram.') }}</p>
      <select-field
        v-model="entity.settings.ad_content_product_set_id"
        :options="sets"
        :reduce="(obj) => obj.id"
        label="name"
        :aria-label="tr('Product set')"
        :disabled="disabled || isRefreshing || sets.length === 0"
        :loading="isLoading || isRefreshing"
      />
    </div>
  </form-block>
</template>

<script>
import RuleFormBlockMixin from '@/mixins/RuleForm/Block.js';
import AdAccountService from '@/services/AdAccount.js';
import RefreshButton from '@/ui/components/RefreshButton.vue';
import SelectField from '@/ui/components/SelectField.vue';

export default {
  name: 'AdContentSourcesBlock',
  components: {
    RefreshButton,
    SelectField,
  },
  mixins: [ RuleFormBlockMixin ],
  props: {
    hideWhenNoCatalogues: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({
    isLoading: false,
    isRefreshing: false,
    catalogues: [],
  }),
  computed: {
    isVisible() {
      return !(this.hideWhenNoCatalogues && this.catalogues.length === 0);
    },
    cataloguesOptions() {
      return [
        { id: null, name: this.tr('Without product catalog') },
        ...this.catalogues,
      ];
    },
    selectedCatalogue() {
      if (this.entity.settings.ad_content_product_catalogue_id === null) {
        return null;
      }

      return this.catalogues.find(c => c.id === this.entity.settings.ad_content_product_catalogue_id);
    },
    sets() {
      return this.selectedCatalogue?.product_sets || [];
    },
    selectedProductSet() {
      if (this.entity.settings.ad_content_product_set_id === null) {
        return null;
      }

      return this.sets.find(s => s.id === this.entity.settings.ad_content_product_set_id);
    },
  },
  watch: {
    isVisible(val) {
      if (!val) {
        this.entity.settings.ad_content_product_catalogue_id = null;
        this.entity.settings.ad_content_product_set_id = null;
      }
    },
    'entity.ad_account.id'() {
      this.catalogues = [];
      this.fetch();
    },
    isLoading(val) {
      if (!val) {
        this.clearUnavailableValues();
      }
    },
    isRefreshing(val) {
      if (!val) {
        this.clearUnavailableValues();
      }
    },
    sets(val, oldVal) {
      if (JSON.stringify(val) === JSON.stringify(oldVal)) {
        return;
      }

      if (this.sets.length === 0) {
        this.entity.settings.ad_content_product_set_id = null;
      }
      else if (!this.sets.map(s => s.id).includes(this.entity.settings.ad_content_product_set_id)) {
        this.entity.settings.ad_content_product_set_id = this.sets[0]?.id || null;
      }
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    fetch() {
      if (this.entity.ad_account?.id) {
        this.isLoading = true;
        const promise = AdAccountService.getProductCatalogues(this.entity.ad_account.id);
        this.updateCataloguesList(promise)
          /* eslint-disable no-console */
          .catch(err => console.error(err))
          .finally(() => this.isLoading = false);
      }
    },
    validate() {
      if (!this.isVisible) {
        return true;
      }

      if (this.entity.settings.ad_content_product_catalogue_id === null) {
        return true;
      }

      if (!this.selectedCatalogue) {
        this.error(this.tr('Please select a valid catalogue.'));
        return false;
      }

      if (!this.selectedProductSet) {
        this.error(this.tr('Please select a valid product set.'));
        return false;
      }

      return true;
    },
    refreshCatalogues() {
      this.isRefreshing = true;

      const promise = AdAccountService.refreshProductCatalogs(this.entity.ad_account.id);
      return this.updateCataloguesList(promise).finally(() => this.isRefreshing = false);
    },
    updateCataloguesList(apiRequestPromise) {
      return apiRequestPromise
        .then(catalogues => this.catalogues = catalogues)
        .catch(err => this.error(err));
    },
    clearUnavailableValues() {
      if (!this.selectedCatalogue) {
        this.entity.settings.ad_content_product_catalogue_id = null;
      }

      this.$nextTick(() => {
        if (!this.selectedProductSet) {
          this.entity.settings.ad_content_product_set_id = null;
        }
      });
    },
  },
};
</script>
